import { useMemo } from 'react';
import { DEFAULT_CLIENTS_QUERY_LIMIT } from 'src/graphql/entities/client';
import { prepareSortAttributes } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSorting } from 'src/graphql/entities/custom-view/custom-view.types';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { ClientAttributeAccessors, ClientAttributeType } from './constants';

// this hook abstracts the interface required for generic custom views table
export const useClientRecords = ({
  filters: rawFilters,
  sort,
  limit = DEFAULT_CLIENTS_QUERY_LIMIT,
  searchTerm,
  dynamicAttributePaths,
}: {
  filters: ConditionInput[];
  sort?: CustomViewSorting;
  limit?: number;
  searchTerm?: string;
  dynamicAttributePaths?: string[];
}) => {
  const filter = useDebouncedValue(rawFilters, 500);

  const options = useMemo(() => {
    return {
      searchTerm: searchTerm ?? '',
      filter,
      limit,
      sort: prepareSortAttributes<ClientAttributeType>(sort, ClientAttributeAccessors),
      entityName: EntityNames.Client,
      showResultsWhenEmptySearchTerm: true,
      dynamicAttributePaths,
    };
  }, [filter, limit, sort, searchTerm, dynamicAttributePaths]);

  const data = useSearch(options);

  if (!data) {
    throw new Error('Search is not configured to search for clients');
  }

  return data;
};
